'use client';

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { PencilIcon, TrashIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export default function DetailsTab({ project, onUpdateSuccess }) {
  const navigate = useNavigate();
  
  // Initialize the form with the project data.
  const [formData, setFormData] = useState({
    id: project.id,
    name: project.name || null,
    client_name: project.client_name || null,
    address: project.address || null,
    contact_name: project.contact_name || null,
    contact_phone: project.contact_phone || null,
    contact_email: project.contact_email || null,
    start_date: project.start_date || null,
    end_date: project.end_date || null,
    description: project.description || null,
    active: project.active !== undefined ? project.active : true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Handle changes for each input field.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update the project record in the projects table.
  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          name: formData.name,
          client_name: formData.client_name,
          address: formData.address,
          contact_name: formData.contact_name,
          contact_phone: formData.contact_phone,
          contact_email: formData.contact_email,
          start_date: formData.start_date,
          end_date: formData.end_date,
          description: formData.description,
          active: formData.active,
        })
        .eq('id', project.id);
      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage('Changes saved successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        if (typeof onUpdateSuccess === 'function') {
          onUpdateSuccess();
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setEditMode(false);
    }
  };

  // Cancel edit mode
  const handleCancel = () => {
    setFormData({
      id: project.id,
      name: project.name || null,
      client_name: project.client_name || null,
      address: project.address || null,
      contact_name: project.contact_name || null,
      contact_phone: project.contact_phone || null,
      contact_email: project.contact_email || null,
      start_date: project.start_date || null,
      end_date: project.end_date || null,
      description: project.description || null,
      active: project.active !== undefined ? project.active : true,
    });
    setEditMode(false);
    setError('');
  };

  // Delete the project from the database
  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', project.id);
      
      if (error) {
        setError(error.message);
      } else {
        navigate('/projects');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while deleting the project');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Section Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Project Information</h2>
            <p className=" text-sm text-gray-500">
              Manage project details and settings
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  onClick={handleUpdate}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                onClick={() => setEditMode(true)}
                disabled={loading}
              >
                <PencilIcon className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mx-6 mt-4 px-3 py-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mx-6 mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Form Content */}
      <div className="p-6 space-y-8">
        {/* Status and Name */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="active" className="block text-xs font-medium text-gray-700 mb-1.5">
              Status
            </label>
            <div className="flex rounded-md shadow-sm">
              <button
                type="button"
                onClick={() => editMode && handleChange({ target: { name: 'active', value: true } })}
                disabled={!editMode}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-l-md border transition-all ${
                  formData.active
                    ? 'bg-green-50 border-green-500 text-green-700 z-10 ring-1 ring-green-500'
                    : editMode 
                      ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      : 'bg-gray-50 border-gray-200 text-gray-400'
                } ${!editMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => editMode && handleChange({ target: { name: 'active', value: false } })}
                disabled={!editMode}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-r-md border -ml-px transition-all ${
                  !formData.active
                    ? 'bg-red-50 border-red-500 text-red-700 z-10 ring-1 ring-red-500'
                    : editMode
                      ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      : 'bg-gray-50 border-gray-200 text-gray-400'
                } ${!editMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Inactive
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1.5">
              Project Name
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                !editMode 
                  ? 'bg-gray-50 border-gray-200 text-gray-500' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
        </div>

        {/* Client Information */}
        <div>
          <h3 className="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">Client Details</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="client_name" className="block text-xs font-medium text-gray-700 mb-1.5">
                Client Name
              </label>
              <input
                id="client_name"
                type="text"
                name="client_name"
                value={formData.client_name || ''}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                  !editMode 
                    ? 'bg-gray-50 border-gray-200 text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-xs font-medium text-gray-700 mb-1.5">
                Address
              </label>
              <input
                id="address"
                type="text"
                name="address"
                value={formData.address || ''}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                  !editMode 
                    ? 'bg-gray-50 border-gray-200 text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h3 className="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label htmlFor="contact_name" className="block text-xs font-medium text-gray-700 mb-1.5">
                Name
              </label>
              <input
                id="contact_name"
                type="text"
                name="contact_name"
                value={formData.contact_name || ''}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                  !editMode 
                    ? 'bg-gray-50 border-gray-200 text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label htmlFor="contact_phone" className="block text-xs font-medium text-gray-700 mb-1.5">
                Phone
              </label>
              <input
                id="contact_phone"
                type="tel"
                name="contact_phone"
                value={formData.contact_phone || ''}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                  !editMode 
                    ? 'bg-gray-50 border-gray-200 text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label htmlFor="contact_email" className="block text-xs font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                id="contact_email"
                type="email"
                name="contact_email"
                value={formData.contact_email || ''}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                  !editMode 
                    ? 'bg-gray-50 border-gray-200 text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div>
          <h3 className="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">Timeline</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="start_date" className="block text-xs font-medium text-gray-700 mb-1.5">
                Start Date
              </label>
              <input
                id="start_date"
                type="date"
                name="start_date"
                value={formData.start_date ? formData.start_date.split('T')[0] : ''}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                  !editMode 
                    ? 'bg-gray-50 border-gray-200 text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>

            <div>
              <label htmlFor="end_date" className="block text-xs font-medium text-gray-700 mb-1.5">
                End Date
              </label>
              <input
                id="end_date"
                type="date"
                name="end_date"
                value={formData.end_date ? formData.end_date.split('T')[0] : ''}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                  !editMode 
                    ? 'bg-gray-50 border-gray-200 text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description || ''}
            onChange={handleChange}
            disabled={!editMode}
            rows={3}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition-all resize-none ${
              !editMode 
                ? 'bg-gray-50 border-gray-200 text-gray-500' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          ></textarea>
        </div>
      </div>

      {/* Delete Section - Danger Zone */}
      <div className="bg-red-50 border-t border-red-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-red-900">Danger Zone</h3>
            <p className="mt-0.5 text-xs text-red-700">
              Permanently remove this project and unlink all measurements
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-100 hover:border-red-400 transition-colors"
            disabled={loading}
          >
            Delete Project
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div 
              className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowDeleteModal(false)}
            ></div>

            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-medium text-gray-900">
                      Delete Project
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete "{formData.name || 'this project'}"? This action cannot be undone.
                      </p>
                      <p className="mt-2 text-xs text-gray-400">
                        All measurements will be unlinked and moved to unmapped.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}